// src/lib/posthog/login-events.ts
//
// Pure decision rules for the login / 2FA lifecycle events: `login`,
// `login_failed` (`reason_code`), `two_factor_prompted` / `two_factor_completed`
// (`method`). No `window`, no `authClient` import, no `capture` call:
// `login-form.tsx` and the `2fa/*` pages own the wiring and call these
// functions with plain values, so the decisions stay testable with literal
// tables — this repo's Vitest config runs with `environment: "node"` (no
// jsdom), so nothing inside a `.tsx` is reachable by a test.

// `login_failed.reason_code` and `signup_failed.reason_code` are both Better
// Auth's error `code` string, passed through unchanged, so both events stay
// comparable in one funnel. The mapper lives in
// `signup-events.ts` and is imported here rather than duplicated: one
// implementation for both events is the whole point of the rule, and a
// second copy would only need to drift once to break the funnel.
import { betterAuthReasonCode } from "./signup-events";

/**
 * The `login_failed` mapper, for both the `res.error` branch of
 * `authClient.signIn.email` and the outer try/catch's thrown value.
 * `error.message` is never read here either — see `betterAuthReasonCode`.
 */
export function loginFailureReason(error: unknown): { reason_code: string } {
  return { reason_code: betterAuthReasonCode(error) };
}

/**
 * `two_factor_completed.method`: `/api/2fa/verify`
 * responds with `source: "primary" | "device"`; only the exact string
 * `"device"` maps to `"device"`, everything else (including `"primary"`,
 * a missing field, or a malformed response) falls back to `"totp"` — the
 * page always asked for a TOTP code, so an unrecognised source is never
 * silently misreported as a device match.
 */
export function twoFactorMethod(source: unknown): "totp" | "device" {
  return source === "device" ? "device" : "totp";
}
